import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';

// Pages
import Login from './pages/Login';
import Home from './pages/Home';
import LandingPage from './pages/LandingPage';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Support from './pages/Support';
import VideoRoom from './pages/VideoRoom';
import TestMeetingHistory from './pages/TestMeetingHistory';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import AssessmentDashboard from './pages/AssessmentDashboard';
import CreateAssessment from './pages/CreateAssessment';
import TakeAssessment from './pages/TakeAssessment';
import MonitorProctoring from './pages/MonitorProctoring';
import AssessmentMonitorRoom from './pages/AssessmentMonitorRoom';
import AssessmentResults from './pages/AssessmentResults';

// Components
import Navbar from './components/Navbar';
import TestComponent from './components/TestComponent';

// Context
import { AuthProvider } from './context/AuthContext';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Home Route Component - Shows landing page for non-authenticated, dashboard for authenticated
const HomeRoute = () => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  
  if (isAuthenticated) {
    return (
      <>
        <Navbar />
        <Home />
      </>
    );
  }
  
  return <LandingPage />;
};

const BASE_URL = 'https://procomm-india.vercel.app';

const getSeoForPath = (pathname) => {
  if (pathname === '/privacy') {
    return {
      title: 'Privacy Policy | ProComm',
      description: 'Read the privacy policy for ProComm video conferencing platform.',
      keywords: 'ProComm privacy, ProComm policy, ProComm',
      robots: 'index, follow'
    };
  }

  if (pathname === '/terms') {
    return {
      title: 'Terms of Service | ProComm',
      description: 'Read the terms of service for ProComm video conference platform.',
      keywords: 'ProComm terms, ProComm policy, ProComm',
      robots: 'index, follow'
    };
  }

  if (pathname === '/cookies') {
    return {
      title: 'Cookie Policy | ProComm',
      description: 'Read the cookie policy for ProComm.',
      keywords: 'ProComm cookies, ProComm policy, ProComm',
      robots: 'index, follow'
    };
  }

  if (pathname === '/login') {
    return {
      title: 'Login | ProComm',
      description: 'Sign in to ProComm and manage your video meetings and collaboration.',
      keywords: 'ProComm login, ProComm sign in, ProComm',
      robots: 'noindex, nofollow'
    };
  }

  if (pathname.startsWith('/room/') || pathname.startsWith('/assessments') || pathname.startsWith('/profile') || pathname.startsWith('/settings') || pathname.startsWith('/support')) {
    return {
      title: 'ProComm Workspace',
      description: 'Private ProComm workspace and meeting content.',
      keywords: 'ProComm meeting workspace',
      robots: 'noindex, nofollow'
    };
  }

  return {
    title: 'ProComm | Professional Video Conference Platform',
    description: 'ProComm is a professional video conference platform for secure online meetings, chat, screen sharing, and collaboration.',
    keywords: 'ProComm, procomm, ProComm Video Conference, video conferencing, online meetings, WebRTC',
    robots: 'index, follow'
  };
};

const upsertMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      if (key !== 'content') {
        tag.setAttribute(key, value);
      }
    });
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', attributes.content);
};

const upsertCanonical = (href) => {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(location.pathname);
    const canonicalUrl = `${BASE_URL}${location.pathname === '/' ? '/' : location.pathname}`;

    document.title = seo.title;
    upsertCanonical(canonicalUrl);

    upsertMetaTag('meta[name="description"]', { name: 'description', content: seo.description });
    upsertMetaTag('meta[name="keywords"]', { name: 'keywords', content: seo.keywords });
    upsertMetaTag('meta[name="robots"]', { name: 'robots', content: seo.robots });

    upsertMetaTag('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsertMetaTag('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsertMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });

    upsertMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
    upsertMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-[#0f172a] dark:text-gray-100 transition-colors duration-300 ease-in-out">
        <Router>
          <SeoManager />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Navbar />
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Navbar />
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <Navbar />
                <Support />
              </ProtectedRoute>
            } />
            <Route path="/room/:roomId" element={
              <ProtectedRoute>
                <VideoRoom />
              </ProtectedRoute>
            } />
            <Route path="/test" element={<TestComponent />} />
            <Route path="/test-meeting-history" element={
              <ProtectedRoute>
                <Navbar />
                <TestMeetingHistory />
              </ProtectedRoute>
            } />
            <Route path="/assessments" element={
              <ProtectedRoute>
                <Navbar />
                <AssessmentDashboard />
              </ProtectedRoute>
            } />
            <Route path="/assessments/create" element={
              <ProtectedRoute>
                <Navbar />
                <CreateAssessment />
              </ProtectedRoute>
            } />
            <Route path="/assessments/edit/:assessmentId" element={
              <ProtectedRoute>
                <Navbar />
                <CreateAssessment />
              </ProtectedRoute>
            } />
            <Route path="/assessments/:assessmentId/take" element={
              <ProtectedRoute>
                <TakeAssessment />
              </ProtectedRoute>
            } />
            <Route path="/assessments/:assessmentId/monitor" element={
              <ProtectedRoute>
                <Navbar />
                <MonitorProctoring />
              </ProtectedRoute>
            } />
            <Route path="/assessments/:assessmentId/monitor/live" element={
              <ProtectedRoute>
                <AssessmentMonitorRoom />
              </ProtectedRoute>
            } />
            <Route path="/assessments/:assessmentId/results/:attemptId" element={
              <ProtectedRoute>
                <Navbar />
                <AssessmentResults />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        <Analytics />
      </div>
    </AuthProvider>
  );
}

export default App;